import {
  Arrow,
  Box,
  BrandsRedIcon,
  Disclaimer,
  Div,
  H5,
  Icon,
  P,
  Pill,
  VerticalSeparator,
  lightTheme,
} from '@vgrubs/components';
import {
  LocationMode,
  SimpleLocationEntity,
} from 'components/src/types/location.types';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocationImage } from './LocationImage';
import { CollapsibleBrands } from './LocationStyled';
import { PushOrdersToPosPill } from './PushOrdersToPosPill';

export const LocationComponent = ({
  location,
  onBeforeNavigate,
}: {
  location: SimpleLocationEntity;
  onBeforeNavigate?: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const elementHeight = ref?.current?.clientHeight;
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const RenderLocationMode = () => {
    return (
      <Pill $background={lightTheme.primary}>
        <Disclaimer $m="0" $color={lightTheme.white} $uppercase>
          POS
        </Disclaimer>
      </Pill>
    );
  };

  return (
    <div>
      <Box
        $flex
        $between
        $alignCenter
        $mb="8"
        $p="12px 20px"
        $pSM="12px 20px"
        $gap="32px"
        onClick={() => {
          onBeforeNavigate?.();
          navigate(`/locations/${location.id}`);
        }}
        $pointer
        $relative
      >
        <Div $flex $gap="16px" $alignCenter $width="100%" $between>
          <Div $flex $gap="16px" $alignCenter $width="100%">
            <Div>
              <LocationImage imageUrl={location.imageUrl} size={50} />
            </Div>
            <Div>
              <H5 $color={expanded && lightTheme.primary} $mb="4">
                {location.name}
              </H5>
              <P $m="0">{location.address}</P>
              {location.mode === LocationMode.pos && (
                <Div $mobile $mt="4">
                  <RenderLocationMode />
                </Div>
              )}
            </Div>
          </Div>
          {location.mode === LocationMode.pos && (
            <Div $desktop>
              <RenderLocationMode />
            </Div>
          )}
        </Div>
        <VerticalSeparator $absolute $top="12px" $bottom="12px" $right="70px" />
        <Icon
          $rotate={expanded ? '180deg' : '0deg'}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          color={lightTheme.darkGray}
          $background={lightTheme.white}
          $size="24"
          hoverBackgroundColor={`${lightTheme.darkGray}1A`}
        >
          <Chevron />
        </Icon>
      </Box>

      <CollapsibleBrands expanded={expanded} elementHeight={elementHeight}>
        <div ref={ref}>
          {location.brands.map(({ name, id, pushOrdersToPos }, i) => {
            return (
              <Box
                $flex
                $between
                $alignCenter
                $mb="8"
                $p="12px 20px"
                $pSM="12px 20px"
                $gap="32px"
                key={i}
                onClick={() => navigate(`/brand/${id}`)}
                $pointer
                $relative
              >
                {/* DESKTOP */}
                <Div $flex $between $alignCenter $desktop $width="100%">
                  <Div $flex $gap="16px" $alignCenter>
                    <Icon>
                      <BrandsRedIcon />
                    </Icon>
                    <H5 $mb="0">{name}</H5>
                  </Div>
                  <PushOrdersToPosPill
                    pushOrdersToPos={pushOrdersToPos}
                    isPos={location.mode === LocationMode.pos}
                  />
                </Div>
                <Div $flex $gap="32px" $alignCenter $desktop>
                  <Icon
                    $background={lightTheme.white}
                    color={lightTheme.primary}
                    $size="20"
                    hoverBackgroundColor={`${lightTheme.primary}1A`}
                  >
                    <Arrow />
                  </Icon>
                </Div>

                {/* MOBILE */}

                <Div $flex $gap="16px" $alignCenter $mobile>
                  <Icon>
                    <BrandsRedIcon />
                  </Icon>
                  <Div $flex $gap="4px" $column>
                    <H5 $mb="0">{name}</H5>
                    <PushOrdersToPosPill
                      pushOrdersToPos={pushOrdersToPos}
                      isPos={location.mode === LocationMode.pos}
                    />
                  </Div>
                </Div>
                <Div $mobile>
                  <Icon
                    $background={lightTheme.white}
                    color={lightTheme.primary}
                    $size="20"
                    hoverBackgroundColor={lightTheme.gray}
                  >
                    <Arrow />
                  </Icon>
                </Div>
                <VerticalSeparator
                  $absolute
                  $top="12px"
                  $bottom="12px"
                  $right="70px"
                />
              </Box>
            );
          })}
          {location.brands.length === 0 ? (
            <P $m="0" $center>
              No brands
            </P>
          ) : (
            ''
          )}
        </div>
      </CollapsibleBrands>
    </div>
  );
};

const Chevron = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.0587 16.8739C11.5793 17.3945 12.4248 17.3945 12.9455 16.8739L19.6095 10.2099C20.1302 9.68922 20.1302 8.84372 19.6095 8.32309C19.0889 7.80246 18.2434 7.80246 17.7228 8.32309L12 14.0459L6.27724 8.32725C5.75661 7.80662 4.9111 7.80662 4.39047 8.32725C3.86984 8.84788 3.86984 9.69339 4.39047 10.214L11.0545 16.8781L11.0587 16.8739Z"
        fill="#4C4C4C"
      />
    </svg>
  );
};
